/**
 * EMOJI ICON PICKER
 *
 * Popover with Colony logo + emoji grid for workspace icon selection.
 * Colony logo renders as an image; all others render as emoji text.
 */

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { WORKSPACE_EMOJIS } from "@/lib/workspace-constants";
import { cn } from "@/lib/utils";
import { useState } from "react";

export function WorkspaceIconPreview({
  icon,
  color,
  size = 40,
  className,
}: {
  icon: string;
  color: string;
  size?: number;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-full flex items-center justify-center shrink-0",
        className
      )}
      style={{ width: size, height: size, backgroundColor: color }}
    >
      {icon === "colony" ? (
        <img
          src="/colony-logo.png"
          alt="Colony"
          className="object-contain"
          style={{ width: size * 0.55, height: size * 0.55 }}
        />
      ) : (
        <span style={{ fontSize: size * 0.55, lineHeight: 1 }}>{icon}</span>
      )}
    </div>
  );
}

interface EmojiIconPickerProps {
  value: string;
  color: string;
  onChange: (icon: string) => void;
  size?: number;
}

export function EmojiIconPicker({ value, color, onChange, size = 40 }: EmojiIconPickerProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Change workspace icon"
          className="rounded-full ring-offset-background transition-all hover:ring-2 hover:ring-ring hover:ring-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 cursor-pointer"
        >
          <WorkspaceIconPreview icon={value} color={color} size={size} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-3" align="start">
        <div className="grid grid-cols-7 gap-1.5">
          {WORKSPACE_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              aria-label={`Select ${emoji} icon`}
              onClick={() => { onChange(emoji); setOpen(false); }}
              className={cn(
                "w-8 h-8 rounded-md flex items-center justify-center transition-all",
                "hover:bg-accent hover:scale-110",
                value === emoji && "bg-accent ring-2 ring-primary ring-offset-1 ring-offset-background"
              )}
            >
              {emoji === "colony" ? (
                <img src="/colony-logo.png" alt="Colony" className="w-5 h-5 object-contain" />
              ) : (
                <span className="text-lg leading-none">{emoji}</span>
              )}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
